import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { reduxForm } from 'redux-form';
import { formSettings } from './../settings';
import { FormattedMessage, FormattedHTMLMessage } from 'react-intl';
import messages from '../../../../../../text/messages';
import Table from './../../../../../../components/table';
import Document from './../../../../../../components/document';
import { getAddress, getFullName, maskSSN } from './../../../../../../utilities/string';
import WizardButtons from '../../../../../../components/wizard/navigator/buttons';
import { executeJobAction } from '../../../../../../reducers/jobs';
import { getRequirements } from '../../../../../../reducers/tenants';
import { completeOnboarding, getPosition, setProgressionProperties } from '../../../../../../reducers/onboarding';
import { history } from '../../../../../../configuration/store';
import { submitOnboarding } from '../../../../../../reducers/identity';
import { dateToFloatingString } from '../../../../../../utilities/dateTime';
import { unsetDocumentId } from '../../../../../../reducers/documents';
import mixpanel from 'mixpanel-browser';

class EmployeeNextStepsStep extends React.Component {
    constructor(props) {
        super(props);

        this.getTableData = this.getTableData.bind(this);
        this.submit = this.submit.bind(this);
        this.renderDocumentControls = this.renderDocumentControls.bind(this);
    }

    componentDidMount() {
        mixpanel.track('WorkerOnboarding', { _data: { branch: 'employee', step: 'EmployeeNextStepsStep' } });

        const { profile, setProgressionProperties } = this.props;

        let personalInformation = {
            firstName: profile.first_name,
            middleName: profile.middle_name,
            lastName: profile.last_name,
            email: profile.email,
            phoneNumber: profile.phone,
            dateOfBirth: profile.birthday_date,
            socialSecurityNumber: maskSSN(profile.ssn_bg_check),
            emergency_name: profile.emergency_name,
            emergency_email: profile.emergency_email,
            emergency_phone: profile.emergency_phone,
            emergency_relationship: profile.emergency_relationship,
            w4_fields: profile.w4_fields,
            i9_fields: profile.i9_fields
        };

        let dateOfBirth = { day: null, month: null, year: null };
        if (personalInformation.dateOfBirth) {
            let date = new Date(personalInformation.dateOfBirth + 'T00:00:00');
            dateOfBirth = {
                date: personalInformation.dateOfBirth,
                day: isNaN(date.getDate()) ? null : date.getDate(),
                month: isNaN(date.getMonth()) ? null : date.getMonth() + 1,
                year: isNaN(date.getFullYear()) ? null : date.getFullYear()
            };
        }

        setProgressionProperties({ personalInformation: { ...personalInformation, dateOfBirth } });
    }

    componentWillUnmount() {
        const { unsetDocumentId } = this.props;
        unsetDocumentId('employee-I9-filled');
    }

    submit() {
        const { role, job, details, executeJobAction, getRequirements, submitOnboarding, completeOnboarding } = this.props;
        executeJobAction(job.id, 'submit', () => {
            submitOnboarding(details, role, () => {
                getRequirements(role, () => {
                    completeOnboarding(() => history.push('/'));
                });
            });
        });
    }

    getTableData() {
        const { job, address, hiring_manager } = this.props;

        let workLocation = <FormattedMessage {...messages.notProvided} />;
        if (job.work_location_type === 'offsite') {
            workLocation = <FormattedMessage {...messages.workLocationAtYourLocation} />;
        } else if (address) {
            workLocation = getAddress(address);
        }

        return {
            columns: [
                { valueMap: d => <span className="row-label">{d.label}</span>, size: 'col-xs-12 col-md-3' },
                { valueMap: d => <span className="row-value">{d.value}</span>, size: 'col-xs-12 col-md-9' }
            ],
            data: [
                { label: <FormattedMessage {...messages.startDate} />, value: dateToFloatingString(job.start_date) },
                { label: <FormattedMessage {...messages.workLocation} />, value: workLocation },
                {
                    label: <FormattedMessage {...messages.pointOfContact} />,
                    value: hiring_manager ? getFullName(hiring_manager) : <FormattedMessage {...messages.notProvided} />
                }
            ]
        };
    }

    renderDocumentControls() {
        return (
            <div className="manage-documents">
                <div className="color-1">
                    <Document id={'employee-I9-filled'} docType={'USA-I9'} view={{ showHeader: false }} />
                </div>
            </div>
        );
    }

    render() {
        const { handleSubmit, onSubmit, back, submitting, position } = this.props;
        if (!position) {
            return null;
        }

        let tableData = this.getTableData();

        return (
            <form onSubmit={handleSubmit(values => this.submit(values, onSubmit))}>
                <div className="row intro-paragraph">
                    <div className="col-xs-12">
                        <FormattedHTMLMessage
                            id={'congrats-intro1'}
                            defaultMessage={"You're done with onboarding - congratulations!  Just two more things now:"}
                            tagName={'div'}
                        />
                    </div>
                </div>
                <div className="row intro-paragraph">
                    <div className="col-xs-12">
                        <FormattedHTMLMessage
                            id={'congrats-intro1'}
                            defaultMessage={
                                "On our side, we're processing your background check.  It normally takes between 3 and 7 days to complete; we'll notify you and your manager just as soon as it's done."
                            }
                            tagName={'div'}
                        />
                    </div>
                </div>
                <div className="row intro-paragraph">
                    <div className="col-xs-12">
                        <FormattedHTMLMessage
                            id={'i9manual.intro2'}
                            defaultMessage={
                                "On your side, you'll need to <strong>download your I-9 below, print it out,</strong> and <strong>get it witnessed</strong>."
                            }
                            tagName={'div'}
                        />
                    </div>
                </div>
                <div className="row intro-paragraph">
                    <div className="col-xs-12">
                        <FormattedHTMLMessage
                            id={'i9manual.intro3'}
                            defaultMessage={
                                'If you will be working onsite, bring it to your manager on or before your first day of work, together with your original proof of eligibility document(s).'
                            }
                            tagName={'div'}
                        />
                    </div>
                </div>
                <div className="row section">
                    <div className="col-xs-12">
                        <FormattedHTMLMessage
                            id={'i9manual.intro4'}
                            defaultMessage={
                                "If you will not be at a company location for your first day of work, you will need to get your I-9 witnessed before your employment start date.  You can either use a notary service near you, or contact us at <a href='mailto:hr@greenlight.ai'>hr@greenlight.ai</a> for other ways to get your I-9 witnessed."
                            }
                            tagName={'div'}
                        />
                    </div>
                </div>
                <div className="row section">
                    <div className="col-md-8 col-xs-12">
                        <Table {...tableData} rowStyle={'onboarding-table-row'} />
                    </div>
                    <div className="col-md-4 col-xs-12">
                        <div className="col-xs-12 onboarding-list-box">
                            <FormattedMessage id={'jobFinish.toBring'} defaultMessage={'Items to Bring With You'} tagName={'h5'} />
                            <p className="onboarding-list-item">
                                <i className="material-icons">check_box</i>
                                <FormattedMessage {...messages.docSignedI9} />
                            </p>
                            <p className="onboarding-list-item">
                                <i className="material-icons">check_box</i>
                                <FormattedMessage {...messages.docGovernmentIssuedId} />
                            </p>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-xs-12 manage-documents-header no-select">
                        <div className="col-sm-5 col-xs-8 column">
                            <span className="ellipsis">
                                <FormattedMessage id={'document'} defaultMessage={'Document'} />
                            </span>
                        </div>
                        <div className="col-xs-3 hidden-xs column">
                            <span className="ellipsis">
                                <FormattedMessage id={'owner'} defaultMessage={'Owner'} />
                            </span>
                        </div>
                        <div className="col-xs-2 hidden-xs column">
                            <span className="ellipsis">
                                <FormattedMessage id={'uploadDate'} defaultMessage={'Upload Date'} />
                            </span>
                        </div>
                        <div className="col-sm-2 col-xs-4 column text-right">
                            <span className="ellipsis">
                                <FormattedMessage id={'actions'} defaultMessage={'Actions'} />
                            </span>
                        </div>
                    </div>
                    <div className="col-xs-12">{this.renderDocumentControls()}</div>
                </div>
                <WizardButtons disabled={submitting} back={{ action: back }} submit={{ message: <FormattedMessage {...messages.complete} /> }} />
            </form>
        );
    }
}

EmployeeNextStepsStep = reduxForm(formSettings)(EmployeeNextStepsStep);

const mapStateToProps = state => {
    return {
        role: state.identity.role,
        profile: state.identity.profile,
        job: state.onboarding.job,
        position: state.onboarding.position
    };
};

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            getPosition,
            executeJobAction,
            getRequirements,
            submitOnboarding,
            unsetDocumentId,
            setProgressionProperties,
            completeOnboarding
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(EmployeeNextStepsStep);



// WEBPACK FOOTER //
// ./src/pages/onboarding/tenants/contractor/steps/employee/employeeNextSteps.jsx